import { isEmpty, isNull } from 'lodash';
import store from '../../store/store';
import { Permissions } from './access-control/enums/permissions';
import hasPermission from './access-control/has-permission';
import { SubscriptionPlans } from './subscription-plans';
import { UserRole } from '../enums/user-roles';

const getState = () => store?.getState();
const defaultPlanCode = SubscriptionPlans.TRIAL;

export const getUserSubscriptionDetails = () => getState().home?.subscription;

export const getUserId = () => getState().home?.userId;

export const getUserFirstName = () => getState().home?.firstName;

export const getUserLastName = () => getState().home?.lastName;

export const getUserPhone = () => getState().myProfile?.myProfile?.phone;

export const getAdminFirstName = () => getState().home?.adminDetails?.firstName;

export const getUserFullName = () =>
  `${getUserFirstName()} ${getUserLastName()}`;

export const getUserEmail = () => getState()?.home?.email;

export const getUserSignUpDate = () => getState().home?.createdAt;

export const getIsNewUser = () => {
  const createdAt = getState().home?.createdAt;

  if (!createdAt) return false;

  const createdDate = new Date(createdAt);
  const now = new Date();
  const diffInMs = now.getTime() - createdDate.getTime();
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
  return diffInDays <= 30;
};

export const getUserTrackingId = () => getState().home?.trackingId;

export const getUserRole = () => getState().home?.role;

export const isAgencyClientUser = () => {
  const role = getUserRole();
  return (
    role === UserRole.ClientFullAccess ||
    role === UserRole.ClientRestrictedAccess
  );
};

export const isAgencyUser = () => getState().home?.isAgencyUser;

export const isAgency = () => !!parseInt(getState().home?.isAgency, 10);

export const getAgencyUserRole = () => isAgencyUser() && getState().home?.role;

export const getAgencyClient = () => getState().home?.client;

export const getAgencyName = () =>
  isAgencyUser() && getState().home?.agencyConfig?.agencyName;

export const getUserCurrentPlan = () =>
  getState().home.subscription?.planCode || defaultPlanCode;

export const getUserUseCase = () => getState().home?.useCase;

export const getUserIndustry = () => getState().home?.industry;

export const getUserCompanySize = () => getState().home?.companySize;

export const getUserJobRole = () => getState().home?.jobRole;

export const getUserPlanStartDate = () => getState().home.subscription?.startAt;

export const getUserPlanEndDate = () =>
  getState().home.subscription?.nextBillingAt;

export const getUserSubscribedAt = () => getState().home.subscription?.startAt;

export const getUserCountry = () => getState().home?.country;

export const getUserPhoneNumber = () => getState().home?.phoneNumber;

export const getUserReferralPage = () => 'www.saleshandy.com';

export const getUserShAccountId = () => getState().home?.shAccountId;

export const getUserPlanId = () => getState().home?.subscription?.planId;

export const getUserPlanCode = () => getState().home?.subscription?.planCode;

export const getUserPlanName = () => getState().home?.subscription?.planName;

export const getUserPlanType = () => getState().home?.subscription?.planType;

export const getUserTimeZone = () => getState().home?.timeZone;

export const getUserFeatureUsages = () => getState().home?.featureUsages;

export const getIsPlanUpgradeScheduled = () =>
  getState().home?.isPlanUpgradeScheduled;

export const getUserLeadFinderPlanName = () =>
  getState().home?.leadFinderSubscription?.planName;

export const getUserLeadFinderPlanCode = () =>
  getState().home?.leadFinderSubscription?.planCode;

export const isMultichannelActive = () =>
  getState()?.home?.isMultichannelActive;

export const leadFinderBulkOperationLimit = () =>
  getState()?.home?.leadFinderLimit;

export const userHasWhitelabelAccessPermission = () =>
  hasPermission(Permissions.WHITELABEL_ACCESS);

export const isWhiteLabel = () =>
  getState()?.home?.agencyConfig?.clientBaseUrl ||
  getState()?.home?.agencyConfig?.icon;

export const isWhitelabelWithoutClientView = () =>
  isNull(getAgencyClient()) || isEmpty(getAgencyClient()) || !isWhiteLabel();

export const isWhitelabelWithClientView = () =>
  (!isNull(getAgencyClient()) || !isEmpty(getAgencyClient())) && isWhiteLabel();

export const getAgencyConfigDetails = () => getState().home.agencyConfig;

export const getWhitelabelCompanyName = () =>
  getState()?.home?.agencyConfig?.companyName;

export const isAgencificationClientOrAgencyUser = () =>
  isAgencyUser() || !!getAgencyClient()?.id;

export const getIntercomHash = () => getState()?.home?.intercomHash;

export const getIsDemoAccount = () => getState()?.home?.isDemoAccount;

export const getUserInboxAnalyzerPlanName = () =>
  getState()?.home?.inboxAnalyzerSubscription?.planName;

export const getUserInboxAnalyzerPlanCode = () =>
  getState()?.home?.inboxAnalyzerSubscription?.planCode;

export const getIsLtdUser = () => getState()?.home?.isLtdUser;

// Check if Intercom should be hidden for specific users
export const shouldHideIntercom = () => {
  const userEmail = getUserEmail();
  const restrictedEmails = [
    'abhishek.tiwary+6219@saleshandy.com',
    'abhishek.tiwary+0309@saleshandy.com',
  ];

  return restrictedEmails.includes(userEmail);
};

// hide side bar tabs for specific user
export const hideSideBarTabs = () => {
  const getShAccountId = getUserShAccountId();
  const userRole = getUserRole();

  const restricted = [{ accountId: 190877, role: 'client-restricted-access' }];

  return restricted.some(
    (item) => item?.accountId === getShAccountId && item?.role === userRole,
  );
};

export const leadFinderViewOnly = () => {
  const getShAccountId = getUserShAccountId();
  const userRole = getUserRole();

  const restricted = [{ accountId: 197372, role: 'client-restricted-access' }];

  return restricted?.some(
    (item) => item?.accountId === getShAccountId && item?.role === userRole,
  );
};

export const isProspectEnrollmentEnabled = () => getState().home?.isProspectEnrollmentEnabled;

export const hasV2LeadFinderAccess = () => getState().home?.hasV2LeadFinderAccess;
